import { Suspense, lazy, memo, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useParams
} from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { getFirebaseAnalytics } from '../lib/firebase';
import AuthGuard from '../auth/AuthGuard';
import DeveloperGuard from '../auth/DeveloperGuard';
import Loader from '../components/Loader';
import { useAuth } from '../auth/AuthContext';
import { ROUTES, LEGACY_ROUTES } from './routes';
import { CompensationErrorBoundary } from '../components/CompensationErrorBoundary';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useDisclaimerStatus } from '../hooks/compensation/useDisclaimerStatus';


// Error pages (lazy loaded like all other components)
const UnifiedProfilePage = lazy(() => import('../pages/profile/UnifiedProfilePage'));
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Dev pages (lazy loaded like all other components)
const DevHealthPage = lazy(() => import('../pages/DevHealthPage'));
const DevConsolePage = lazy(() => import('../pages/DevConsolePage'));

// Public pages (no auth required)
// const LandingPage = lazy(() => import('../pages/website/LandingPage'));
const ClosedBetaPage = lazy(() => import('../pages/website/ClosedBetaPage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage'));
const SignUpChoicePage = lazy(() => import('../pages/SignUpChoicePage'));
const RegisterOwner = lazy(() => import('../pages/RegisterOwner'));
const JoinCompany = lazy(() => import('../pages/JoinCompany'));
const JoinRequestStatusPage = lazy(() => import('../pages/JoinRequestStatusPage'));
const InvitePage = lazy(() => import('../pages/InvitePage'));
const CalendarCallbackPage = lazy(() => import('../pages/CalendarCallbackPage'));

// Kiosk Mode - Public terminal and admin management
const KioskClockInPage = lazy(() => import('../pages/kiosk/KioskClockInPage'));
const QuickClockPage = lazy(() => import('../pages/kiosk/QuickClockPage'));

// Website pages (moved to /pages/website/)
// const ContactPage = lazy(() => import('../pages/website/ContactPage'));
// const AboutPage = lazy(() => import('../pages/website/AboutPage'));
// const FeaturesPage = lazy(() => import('../pages/website/FeaturesPage'));
// const PricingPage = lazy(() => import('../pages/website/PricingPage'));

// Legal pages (GDPR & German law compliance)
const PrivacyPage = lazy(() => import('../pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('../pages/legal/TermsPage'));
const DPAPage = lazy(() => import('../pages/legal/DPAPage'));
const CookiesPage = lazy(() => import('../pages/legal/CookiesPage'));
const ImprintPage = lazy(() => import('../pages/legal/ImprintPage'));

// Protected pages (auth required)
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const EmployeesPage = lazy(() => import('../pages/EmployeesPage'));
const AlumniPage = lazy(() => import('../pages/admin/AlumniPage'));
const LeavePage = lazy(() => import('../pages/LeavePage'));
const TeamForecastPage = lazy(() => import('../pages/TeamForecastPage'));
const LeaveDetails = lazy(() => import('../pages/LeaveDetailsPage'));
// LeaveApprovalsPage - REMOVED (consolidated into ApprovalsHubPage)
const Assistant = lazy(() => import('../pages/AssistantPage'));
// UserSettingsPage - REMOVED (consolidated into Account/Notifications/Security tabs)

// New Settings Pages (Redesigned - Route-based navigation, consolidated to 3 tabs)
const SettingsLayout = lazy(() => import('../layouts/SettingsLayout').then((module) => ({ default: module.SettingsLayout })));
const AccountSettingsPage = lazy(() => import('../pages/Settings/AccountSettingsPage'));
const NotificationsSettingsPage = lazy(() => import('../pages/Settings/NotificationsSettingsPage'));
const SecuritySettingsPage = lazy(() => import('../pages/Settings/SecuritySettingsPage'));
const DelegationSettingsPage = lazy(() => import('../pages/Settings/DelegationSettingsPage').then((module) => ({ default: module.DelegationSettingsPage })));
const SystemMonitoringPage = lazy(() => import('../pages/admin/SystemMonitoringPage'));
// NewAdminDashboardPage - REMOVED (absorbed into DashboardPage owner/hr_admin layout)
// Approvals Hub - Consolidated (TRUE CONSOLIDATION)
// Replaces: ApprovalsHubLayout, UnifiedApprovalsPage, UnifiedApprovalsWorkspace, ApprovalHistoryPage
const ApprovalsHubPage = lazy(() => import('../pages/admin/ApprovalsHubPage').then((module) => ({ default: module.ApprovalsHubPage })));
const FeedbackPage = lazy(() => import('../pages/admin/FeedbackPage'));
const BrandingSettingsPage = lazy(() => import('../pages/tenant/BrandingSettingsPage'));
const BrandingControlPanelPage = lazy(() => import('../pages/admin/BrandingControlPanelPage'));
// Kiosk Hub - Consolidated (TRUE CONSOLIDATION)
// Replaces: KioskHubLayout, KioskOverviewPage, KioskPinManagementPage, KioskMonitoringPage, KioskSettingsPage
const KioskHubPage = lazy(() => import('../pages/admin/KioskHubPage').then((module) => ({ default: module.KioskHubPage })));

// SettingsHubLayout - REMOVED (replaced by consolidated SettingsHubPage)
const SettingsHubPage = lazy(() => import('../pages/admin/settings/SettingsHubPage').then((module) => ({ default: module.SettingsHubPage })));
// ApprovalsHubLayout - REMOVED (replaced by consolidated ApprovalsHubPage)
// UnifiedApprovalsWorkspace - REMOVED (consolidated into ApprovalsHubPage)
// HRBenefitsApprovalPage - REMOVED (consolidated into ApprovalsHubPage)
// ExpenseApprovalsPage - REMOVED (consolidated into ApprovalsHubPage)
const ActivityPage = lazy(() => import('../domain/activity/ActivityPage'));
const AppLayout = lazy(() => import('../components/layout/AppLayout'));
const DirectoryPage = lazy(() => import('../pages/DirectoryPage'));
const DocumentsPage = lazy(() => import('../pages/DocumentsPage'));
const MyDocumentsPage = lazy(() => import('../pages/documents/MyDocumentsPage'));
const DocumentHubPage = lazy(() => import('../pages/documents/DocumentHubPage'));

// Hub Pages (Zone-based navigation)
const MySpaceHubPage = lazy(() => import('../pages/hubs/MySpaceHubPage'));
const MyTeamHubPage = lazy(() => import('../pages/hubs/MyTeamHubPage'));
const AdministrationHubPage = lazy(() => import('../pages/hubs/AdministrationHubPage'));
const DevConsoleHubPage = lazy(() => import('../pages/hubs/DevConsoleHubPage'));

const SetupPage = lazy(() => import('../pages/SetupPage'));
const EmployeeWelcomePage = lazy(() => import('../pages/EmployeeWelcomePage'));
const ReportsPage = lazy(() => import('../pages/ReportsPage'));
const AttendanceReportsPage = lazy(() => import('../pages/AttendanceReportsPage'));


// Help Center - Public Docs Pages (Wikipedia-style documentation)
const DocsLandingPage = lazy(() => import('../pages/docs/DocsLandingPage'));
const DocsArticlePage = lazy(() => import('../pages/docs/DocsArticlePage'));
const DocsCategoryPage = lazy(() => import('../pages/docs/DocsCategoryPage'));

// Dev pages
const DevSeedPage = lazy(() => import('../dev/DevSeedPage'));

// German-compliant time tracking pages
const ShiftTrackingPage = lazy(() => import('../pages/ShiftTrackingPage').then((module) => ({ default: module.ShiftTrackingPage })));
// ManagerShiftApprovalPage - REMOVED (consolidated into ApprovalsHubPage)
const TimeTrackingSettingsPage = lazy(() => import('../pages/TimeTrackingSettingsPage').then((module) => ({ default: module.TimeTrackingSettingsPage })));
// Legacy pages removed - functionality consolidated into TimeAttendanceSettingsPage and OrganizationStructureSettingsPage

// Settings Pages - REMOVED (consolidated into SettingsHubPage which lazy-loads them internally)
// const CompanySettingsPage = lazy(() => import('../pages/admin/settings/CompanySettingsPage'));
// const TimeAttendanceSettingsPage = lazy(() => import('../pages/admin/settings/TimeAttendanceSettingsPage'));
// const OrganizationStructureSettingsPage = lazy(() => import('../pages/admin/settings/OrganizationStructureSettingsPage'));
// const CompanyExportPage = lazy(() => import('../pages/Settings/CompanyExportPage'));
// const WebhooksSettingsPage = lazy(() => import('../pages/Settings/WebhooksSettingsPage'));
const LeavePoliciesPage = lazy(() => import('../pages/Settings/LeavePoliciesPage').then((module) => ({ default: module.LeavePoliciesPage })));
const HolidayCalendarsPage = lazy(() => import('../pages/Settings/HolidayCalendarsPage').then((module) => ({ default: module.HolidayCalendarsPage })));
const ComplianceDashboardPage = lazy(() => import('../pages/compliance/ComplianceDashboardPage'));

// Billing & Stripe integration
const BillingPage = lazy(() => import('../pages/BillingPage'));

// Audit Log - Owner only
const AuditLogPage = lazy(() => import('../pages/admin/AuditLogPage').then((module) => ({ default: module.AuditLogPage })));

// Asset Management - Admin only
const AssetInventoryPage = lazy(() => import('../pages/admin/AssetInventoryPage'));
const AssetDetailPage = lazy(() => import('../pages/admin/AssetDetailPage'));

// Knowledge Base (Phase 2)
const AnnouncementFeedPage = lazy(() => import('../pages/AnnouncementFeedPage'));
const AnnouncementDetailPage = lazy(() => import('../pages/AnnouncementDetailPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const CreateAnnouncementPage = lazy(() => import('../pages/admin/CreateAnnouncementPage'));
// Knowledge Base Hub - TRUE CONSOLIDATION
// Replaces: KnowledgeBasePage, CategoryManagerPage, AnalyticsDashboardPage
const KnowledgeBaseHubPage = lazy(() => import('../pages/admin/KnowledgeBaseHubPage').then((module) => ({ default: module.KnowledgeBaseHubPage })));
// KnowledgeBasePage - REMOVED (consolidated into KnowledgeBaseHubPage)
// CategoryManagerPage - REMOVED (consolidated into KnowledgeBaseHubPage)
// AnalyticsDashboardPage - REMOVED (consolidated into KnowledgeBaseHubPage)
const DocumentEditorPage = lazy(() => import('../pages/admin/DocumentEditorPage'));
const KnowledgeBaseHomePage = lazy(() => import('../pages/KnowledgeBaseHomePage'));
const SearchResultsPage = lazy(() => import('../pages/SearchResultsPage'));
const DocumentViewPage = lazy(() => import('../pages/DocumentViewPage'));
const CategoryBrowsePage = lazy(() => import('../pages/CategoryBrowsePage'));

// Learning Hub - Consolidated (TRUE CONSOLIDATION)
// Replaces: LearningHubLayout, MyLearningPage, CourseCatalogPage, MyCertificatesPage, MyQuizHistoryPage
const LearningHubPage = lazy(() => import('../pages/training/LearningHubPage').then((module) => ({ default: module.LearningHubPage })));
const CourseDetailPage = lazy(() => import('../pages/training/CourseDetailPage'));
const TeamQuizProgressPage = lazy(() => import('../pages/training/TeamQuizProgressPage'));
const CoursePlayerPage = lazy(() => import('../pages/training/CoursePlayerPage'));
const QuizPlayerPage = lazy(() => import('../pages/training/QuizPlayerPage'));
const QuizResultsPage = lazy(() => import('../pages/training/QuizResultsPage'));
const AdminCoursesPage = lazy(() => import('../pages/training/admin/AdminCoursesPage'));
const CourseBuilderPage = lazy(() => import('../pages/training/admin/CourseBuilderPage'));
const QuizBuilderPage = lazy(() => import('../pages/training/admin/QuizBuilderPage'));
const QuizAnalyticsPage = lazy(() => import('../pages/training/admin/QuizAnalyticsPage'));
const TrainingAnalyticsDashboardPage = lazy(() => import('../pages/training/admin/AnalyticsDashboardPage'));
const LMSSettingsPage = lazy(() => import('../pages/admin/LMSSettingsPage'));

// Performance Reviews
const MyReviewsPage = lazy(() => import('../pages/MyReviewsPage'));
const SelfAssessmentPage = lazy(() => import('../pages/SelfAssessmentPage'));
const ManagerReviewPage = lazy(() => import('../pages/ManagerReviewPage'));
const AdminReviewCyclesPage = lazy(() => import('../pages/AdminReviewCyclesPage'));

// Goals & OKRs
const MyGoalsPage = lazy(() => import('../pages/goals/MyGoalsPage'));
const GoalDetailPage = lazy(() => import('../pages/goals/GoalDetailPage'));
const CreateGoalPage = lazy(() => import('../pages/goals/CreateGoalPage'));
const TeamGoalsPage = lazy(() => import('../pages/goals/TeamGoalsPage'));
const GoalsAdminPage = lazy(() => import('../pages/goals/GoalsAdminPage'));
const ManageCyclesPage = lazy(() => import('../pages/goals/ManageCyclesPage'));

// Permissions Management (Delegated permissions beyond role)
const PermissionsPage = lazy(() => import('../pages/admin/PermissionsPage'));

// Promo Codes Management (Internal admin - @etchare.com only)
const PromoCodesPage = lazy(() => import('../pages/admin/PromoCodesPage'));

// Founding Customers Management (Internal admin - @etchare.com only)
const FoundingCustomersPage = lazy(() => import('../pages/admin/FoundingCustomersPage'));

// Shift Templates - Recurring shift patterns
const SchedulePage = lazy(() => import('../pages/SchedulePage'));
const MySchedulePage = lazy(() => import('../pages/MySchedulePage'));
// WeeklySchedulePage and ShiftTemplatesPage are rendered as tabs inside SchedulePage
// Standalone routes redirect to /dashboard/scheduling/overview?tab=weekly|templates

// MyTeamDashboardPage - REMOVED (absorbed into DashboardPage manager layout)

// Benefits Admin Hub - TRUE CONSOLIDATION
// Replaces: BenefitsAdminPage, EnrollmentHistoryPage
const BenefitsAdminHubPage = lazy(() => import('../pages/admin/BenefitsAdminHubPage').then((module) => ({ default: module.BenefitsAdminHubPage })));
// EnrollmentHistoryPage - REMOVED (consolidated into BenefitsAdminHubPage)
// BenefitsAdminPage - REMOVED (consolidated into BenefitsAdminHubPage)
const BenefitTypeFormPage = lazy(() => import('../pages/admin/BenefitTypeFormPage'));

// Benefits Management - Employee Routes
const BenefitsEnrollmentPage = lazy(() => import('../pages/Benefits/BenefitsEnrollmentPage'));
const DependentsPage = lazy(() => import('../pages/Benefits/DependentsPage'));
const MyBenefitsPage = lazy(() => import('../pages/Benefits/MyBenefitsPage'));

// Developer Access Management
const DeveloperAccessPage = lazy(() => import('../pages/admin/DeveloperAccessPage'));

// Super Admin Pages
const FeatureFlagsPage = lazy(() => import('../pages/superadmin/FeatureFlagsPage').then((module) => ({ default: module.FeatureFlagsPage })));

// Website CMS Admin Pages
// const CMSManagementPage = lazy(() => import('../pages/admin/CMSManagementPage'));
// const CMSLandingEditorPage = lazy(() => import('../pages/admin/CMSLandingEditorPage'));
// const CMSPricingEditorPage = lazy(() => import('../pages/admin/CMSPricingEditorPage'));
// const CMSFeaturesEditorPage = lazy(() => import('../pages/admin/CMSFeaturesEditorPage'));
// const CMSAboutEditorPage = lazy(() => import('../pages/admin/CMSAboutEditorPage'));
// const CMSContactEditorPage = lazy(() => import('../pages/admin/CMSContactEditorPage'));
// const CMSGlobalEditorPage = lazy(() => import('../pages/admin/CMSGlobalEditorPage'));
// const GrantCMSAccessPage = lazy(() => import('../pages/admin/GrantCMSAccessPage'));

// Visual WYSIWYG CMS Editor
// const VisualCMSEditorPage = lazy(() => import('../pages/admin/cms/visual/VisualCMSEditorPage'));

// Inline CMS Editor (Full-page editing)
// const EditWebsitePage = lazy(() => import('../pages/admin/cms/EditWebsitePage'));

// Finance Hub - Payroll only (Phase 2: compensation moved to CompensationHubPage)
const FinanceHubPage = lazy(() => import('../pages/admin/FinanceHubPage').then((module) => ({ default: module.FinanceHubPage })));

// Payroll Export Disclaimer — one-time acknowledgment gate (Core+, all admin roles)
const PayrollDisclaimerPage = lazy(() => import('../pages/admin/PayrollDisclaimerPage'));

// Compensation Hub - Tip Pools (Operations+), Bonuses/Commissions/Analytics (Growth)
const CompensationHubPage = lazy(() => import('../pages/admin/CompensationHubPage').then((module) => ({ default: module.CompensationHubPage })));

// Finance/Compensation - Detail/Form Pages (outside hub, accessed from hub)
const CompensationDisclaimerPage = lazy(() => import('../pages/admin/CompensationDisclaimerPage'));
const CompensationSettingsPage = lazy(() => import('../pages/admin/CompensationSettingsPage'));
const CompensationReportsPage = lazy(() => import('../pages/admin/CompensationReportsPage'));
const CommissionPlanFormPage = lazy(() => import('../pages/admin/CommissionPlanFormPage'));
const CommissionPlanViewPage = lazy(() => import('../pages/admin/CommissionPlanViewPage'));
const BonusDetailPage = lazy(() => import('../pages/admin/BonusDetailPage'));
const EmployeeBonusesPage = lazy(() => import('../pages/employee/EmployeeBonusesPage'));
const TipPoolFormPage = lazy(() => import('../pages/admin/TipPoolFormPage'));
const TipPoolViewPage = lazy(() => import('../pages/admin/TipPoolViewPage'));

// ATS/Recruiting Hub
// Recruiting Hub - Consolidated (TRUE CONSOLIDATION)
// Replaces: RecruitingHubLayout, JobListPage, CandidateListPage, PipelineBoardPage, InterviewListPage, OfferListPage, TalentPoolPage
const RecruitingHubPage = lazy(() => import('../pages/recruiting/RecruitingHubPage').then((module) => ({ default: module.RecruitingHubPage })));
// RecruitingHubLayout - REMOVED (replaced by consolidated RecruitingHubPage)
// JobListPage - REMOVED (consolidated into RecruitingHubPage)
// CandidateListPage - REMOVED (consolidated into RecruitingHubPage)
// PipelineBoardPage - REMOVED (consolidated into RecruitingHubPage)
// InterviewListPage - REMOVED (consolidated into RecruitingHubPage)
// OfferListPage - REMOVED (consolidated into RecruitingHubPage)
// TalentPoolPage - REMOVED (consolidated into RecruitingHubPage)
const CreateJobPage = lazy(() => import('../pages/recruiting/CreateJobPage'));
const JobDetailPage = lazy(() => import('../pages/recruiting/JobDetailPage'));
const CandidateDetailPage = lazy(() => import('../pages/recruiting/CandidateDetailPage'));
const ScheduleInterviewPage = lazy(() => import('../pages/recruiting/ScheduleInterviewPage'));
const CreateOfferPage = lazy(() => import('../pages/recruiting/CreateOfferPage'));

// Expense Management (Phase 1.1 - German-compliant expense reporting)
const MyExpenseReportsPage = lazy(() => import('../pages/expenses/MyExpenseReportsPage'));
const ExpenseReportDetailPage = lazy(() => import('../pages/expenses/ExpenseReportDetailPage'));
const CreateExpenseReportPage = lazy(() => import('../pages/expenses/CreateExpenseReportPage'));
const ExpenseAnalyticsPage = lazy(() => import('../pages/expenses/ExpenseAnalyticsPage'));
const ExpenseSettingsPage = lazy(() => import('../pages/admin/ExpenseSettingsPage'));
const ExpensesHubPage = lazy(() => import('../pages/admin/ExpensesHubPage').then((m) => ({ default: m.ExpensesHubPage })));

// Surveys (Secure Employee Surveys)
const MySurveysPage = lazy(() => import('../pages/surveys/MySurveysPage'));
const SurveyBuilderPage = lazy(() => import('../pages/surveys/SurveyBuilderPage'));
const SurveyDetailPage = lazy(() => import('../pages/surveys/SurveyDetailPage'));
const SurveyTakingPage = lazy(() => import('../pages/surveys/SurveyTakingPage'));
const SurveyAnalyticsPage = lazy(() => import('../pages/surveys/SurveyAnalyticsPage'));

function withSuspense(element: React.ReactElement) {
  // Validate element is not undefined before wrapping
  if (!element || element.type === undefined) {
    throw new Error(`[Router] Invalid element passed to withSuspense: element is ${element === null ? 'null' : 'undefined'}`);
  }
  return <Suspense fallback={<Loader />}>{element}</Suspense>;
}

function withCompensationProtection(element: React.ReactElement) {
  // Wrap compensation pages with specialized error boundary and suspense
  if (!element || element.type === undefined) {
    throw new Error(`[Router] Invalid element passed to withCompensationProtection: element is ${element === null ? 'null' : 'undefined'}`);
  }
  return (
    <CompensationErrorBoundary>
      <Suspense fallback={<Loader />}>{element}</Suspense>
    </CompensationErrorBoundary>
  );
}

/**
 * Wrap a component with ErrorBoundary and Suspense (memoized to prevent memory leaks)
 *
 * CRITICAL FIX: Memoized to prevent re-creation on every render.
 * Without memoization, listeners inside wrapped components may not cleanup properly,
 * causing memory leaks (especially in React StrictMode which double-mounts components).
 *
 * @param Component - The component to wrap
 * @param name - Display name for debugging
 * @returns Memoized wrapped component
 *
 * NOTE: Ready for incremental rollout to major routes. Not yet applied but infrastructure is complete.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function withErrorBoundary(
  Component: React.ComponentType,
  name: string
): React.ReactElement {
  const WrappedComponent = memo(() => (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  ));

  WrappedComponent.displayName = `withErrorBoundary(${name})`;

  return <WrappedComponent />;
}

function TenantGuard({ children }: { children: React.ReactElement }) {
  const { claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  // If user is signed in but has no tenant context, they must choose a path:
  // - Owner: register company
  // - Employee: join company (invite)
  //
  // IMPORTANT: Do NOT redirect to /dashboard/setup here. Setup requires a tenant and will redirect to /register-owner,
  // which creates a deterministic loop for invited employees.
  if (!claims?.tenantId) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

export function RolesRouteGuard({ children }: { children: React.ReactElement }) {
  const { claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  const role = claims?.role;

  // Allow both owners and HR admins to access roles page
  if (role === 'owner' || role === 'hr_admin') {
    return children;
  }

  return <Navigate to="/unauthorized" replace state={{ from: location }} />;
}

export function AdminRouteGuard({ children }: { children: React.ReactElement }) {
  const { claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  const role = claims?.role;

  if (role === 'owner' || role === 'hr_admin') {
    return children;
  }

  return <Navigate to="/unauthorized" replace state={{ from: location }} />;
}

export function OwnerRouteGuard({ children }: { children: React.ReactElement }) {
  const { claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  const role = claims?.role;

  if (role === 'owner') {
    return children;
  }

  return <Navigate to="/unauthorized" replace state={{ from: location }} />;
}

export function ManagerPlusRouteGuard({ children }: { children: React.ReactElement }) {
  const { claims, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loader />;
  }

  const role = claims?.role;

  if (role === 'owner' || role === 'hr_admin' || role === 'manager') {
    return children;
  }

  return <Navigate to="/unauthorized" replace state={{ from: location }} />;
}

/**
 * CompensationDisclaimerGuard
 *
 * Route guard that ensures users have acknowledged the compensation disclaimer
 * before accessing any compensation management features (except the disclaimer page itself).
 *
 * This guard:
 * 1. Uses cached disclaimer status via useDisclaimerStatus hook (shared across all pages)
 * 2. Redirects to disclaimer page if not acknowledged
 * 3. Allows access only after consent is given and logged
 */
export function CompensationDisclaimerGuard({ children }: { children: React.ReactElement }) {
  const location = useLocation();
  const { claims, loading: authLoading } = useAuth();

  // Use cached disclaimer status - shared across all compensation pages
  const { data: disclaimerData, isLoading: disclaimerLoading } = useDisclaimerStatus({ tenantId: claims?.tenantId });
  const acknowledged = disclaimerData?.acknowledged ?? false;

  if (authLoading || disclaimerLoading) {
    return <Loader />;
  }

  // If not acknowledged, redirect to disclaimer page
  if (!acknowledged) {
    return (
      <Navigate
        to={ROUTES.ADMIN.COMPENSATION.DISCLAIMER}
        replace
        state={{ from: location, requiresConsent: true }}
      />
    );
  }

  return children;
}

// Redirect to current user's profile
function RedirectToMyProfile() {
  const { claims } = useAuth();
  if (claims?.employeeId) {
    return <Navigate to={ROUTES.PEOPLE_PROFILE(claims.employeeId)} replace />;
  }
  return <Navigate to="/dashboard/directory" replace />;
}
function RedirectEmployeeProfile() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to={ROUTES.ADMIN.EMPLOYEES} replace />;
  return <Navigate to={ROUTES.PEOPLE_PROFILE(id)} replace />;
}



function RedirectLeaveDetails() {
  const { id } = useParams();
  return <Navigate to={`/dashboard/leave/${id}`} replace />;
}

// UnifiedProfileRoute removed - Feature Flag forced enabled (V3 Launch)

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    const ga = getFirebaseAnalytics();
    if (!ga) return;
    logEvent(ga, 'page_view', {
      page_path: location.pathname,
      page_title: document.title,
    });
  }, [location.pathname]);
  return <Outlet />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AnalyticsTracker />}>
      {/* Public Routes */}
      <Route path="/" element={withSuspense(<ClosedBetaPage />)} />
      <Route path="/login" element={withSuspense(<LoginPage />)} />
      <Route path="/forgot-password" element={withSuspense(<ForgotPasswordPage />)} />
      <Route path="/signup" element={withSuspense(<SignUpChoicePage />)} />
      <Route path="/register-owner" element={withSuspense(<RegisterOwner />)} />
      <Route path="/join-company" element={withSuspense(<JoinCompany />)} />
      <Route path="/join-request-status/:tenantId" element={withSuspense(<JoinRequestStatusPage />)} />
      <Route path="/invite/:token" element={withSuspense(<InvitePage />)} />
      <Route path="/invite" element={<Navigate to="/join-company?invite=1" replace />} />

      {/* OAuth Callbacks */}
      <Route path="/calendar-callback" element={withSuspense(<CalendarCallbackPage />)} />

      {/* Kiosk Terminal - Public self-service clock-in (no auth required) */}
      {/* S-19: ErrorBoundary prevents blank white screen on render crash */}
      <Route path="/kiosk/:tenantId" element={
        <ErrorBoundary>
          {withSuspense(<KioskClockInPage />)}
        </ErrorBoundary>
      } />

      {/* Quick Clock - Employee scans kiosk QR with phone (requires Firebase auth) */}
      <Route path="/quick-clock/:tenantId" element={
        <ErrorBoundary>
          {withSuspense(<QuickClockPage />)}
        </ErrorBoundary>
      } />

      {/* Website / Marketing Pages */}
      {/* Website / Marketing Pages - DISABLED for Closed Beta (Redirect to /) */}
      <Route path="/contact" element={<Navigate to="/" replace />} />
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/features" element={<Navigate to="/" replace />} />
      <Route path="/pricing" element={<Navigate to="/" replace />} />

      {/* Legal Pages (GDPR & German Law Compliance) */}
      <Route path="/privacy" element={withSuspense(<PrivacyPage />)} />
      <Route path="/terms" element={withSuspense(<TermsPage />)} />
      <Route path="/dpa" element={withSuspense(<DPAPage />)} />
      <Route path="/cookies" element={withSuspense(<CookiesPage />)} />
      <Route path="/imprint" element={withSuspense(<ImprintPage />)} />
      <Route path="/impressum" element={<Navigate to="/imprint" replace />} /> {/* German redirect */}


      <Route path="/unauthorized" element={withSuspense(<UnauthorizedPage />)} />
      <Route path="/404" element={withSuspense(<NotFoundPage />)} />

      {/* Inline CMS Editor - Full page (no dashboard layout) */}
      {/* Inline CMS Editor - DISABLED for Launch (Standardizing on Hardcoded Content) */}
      {/* <Route
        path="/edit-website"
        element={
          <AuthGuard>
            {withSuspense(<EditWebsitePage />)}
          </AuthGuard>
        }
      /> */}

      {/* Setup Page - Full-screen onboarding wizard (no sidebar) */}
      {/* Requires auth but NOT inside AppLayout to prevent sidebar escape */}
      <Route
        path="/dashboard/setup"
        element={
          <AuthGuard>
            {withSuspense(<SetupPage />)}
          </AuthGuard>
        }
      />

      {/* Unified Layout - All authenticated routes use AppLayout with Sidebar */}
      <Route
        element={
          <AuthGuard>
            <TenantGuard>{withSuspense(<AppLayout />)}</TenantGuard>
          </AuthGuard>
        }
      >
        {/* Main Dashboard */}
        <Route path="/dashboard" element={withSuspense(<DashboardPage />)} />

        {/* Hub Pages (Zone-based navigation) */}
        <Route path={ROUTES.HUBS.MY_SPACE} element={withSuspense(<MySpaceHubPage />)} />
        <Route path={ROUTES.HUBS.MY_TEAM} element={withSuspense(<MyTeamHubPage />)} />
        <Route
          path={ROUTES.HUBS.ADMINISTRATION}
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<AdministrationHubPage />)}
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path={ROUTES.HUBS.DEV_CONSOLE}
          element={
            <DeveloperGuard>
              {withSuspense(<DevConsoleHubPage />)}
            </DeveloperGuard>
          }
        />

        {/* Employee Directory - Available to all authenticated users */}
        <Route path="/dashboard/directory" element={withSuspense(<DirectoryPage />)} />


        {/* Unified Profile - Replaces old Social Profile and Edit Profile */}
        <Route path="/dashboard/people/:id" element={withSuspense(<UnifiedProfilePage />)} />

        {/* Legacy Redirections for V3 Migration */}



        {/* Admin Routes */}
        {/* New standardized route */}
        <Route
          path={ROUTES.ADMIN.EMPLOYEES}
          element={
            <AdminRouteGuard>
              {withSuspense(<EmployeesPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Alumni (Terminated Employees) - Admin only */}
        <Route
          path={ROUTES.ADMIN.PEOPLE.ALUMNI}
          element={
            <AdminRouteGuard>
              {withSuspense(<AlumniPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Legacy route redirect (backward compatibility) */}
        <Route
          path={LEGACY_ROUTES.EMPLOYEES_OLD}
          element={<Navigate to={ROUTES.ADMIN.EMPLOYEES} replace />}
        />
        <Route
          path="/dashboard/admin/employees"
          element={<Navigate to={ROUTES.ADMIN.EMPLOYEES} replace />}
        />

        <Route
          path="/dashboard/reports"
          element={
            <AdminRouteGuard>
              {withSuspense(<ReportsPage />)}
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/reports/attendance"
          element={
            <AdminRouteGuard>
              {withSuspense(<AttendanceReportsPage />)}
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/reports/time"
          element={<Navigate to="/dashboard/reports?tab=time" replace />}
        />

        <Route path="/dashboard/settings" element={withSuspense(<SettingsLayout />)}>
          <Route index element={<Navigate to="account" replace />} />
          <Route path="account" element={withSuspense(<AccountSettingsPage />)} />
          <Route path="notifications" element={withSuspense(<NotificationsSettingsPage />)} />
          <Route path="security" element={withSuspense(<SecuritySettingsPage />)} />
          {/* Legacy routes - redirect to new tabs */}
          <Route path="profile" element={<Navigate to="/dashboard/settings/account" replace />} />
          <Route path="preferences" element={<Navigate to="/dashboard/settings/account" replace />} />
          <Route path="connected-apps" element={<Navigate to="/dashboard/settings/security" replace />} />
          <Route path="delegations" element={withSuspense(<DelegationSettingsPage />)} />
        </Route>

        {/* Legacy Settings route removed - consolidated into 3-tab settings */}

        {/* Admin Dashboard - Redirect to unified Dashboard (absorbed into persona layouts) */}
        <Route
          path="/dashboard/admin"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* Feedback - DEVELOPER ONLY (cross-tenant data, internal tool) */}
        <Route
          path="/dashboard/admin/feedback"
          element={
            <DeveloperGuard>
              {withSuspense(<FeedbackPage />)}
            </DeveloperGuard>
          }
        />

        {/* ====================================================== */}
        {/* Approvals Hub - Consolidated (TRUE CONSOLIDATION) */}
        {/* Managers need access to approve leave/time for their team */}
        {/* ====================================================== */}
        <Route
          path="/dashboard/admin/approvals"
          element={
            <ManagerPlusRouteGuard>
              <ErrorBoundary>
                {withSuspense(<ApprovalsHubPage />)}
              </ErrorBoundary>
            </ManagerPlusRouteGuard>
          }
        />
        {/* Approvals Hub Legacy Redirects */}
        <Route path="/dashboard/admin/approvals/leave" element={<Navigate to="/dashboard/admin/approvals?tab=leave" replace />} />
        <Route path="/dashboard/admin/approvals/shifts" element={<Navigate to="/dashboard/admin/approvals?tab=shifts" replace />} />
        <Route path="/dashboard/admin/approvals/benefits" element={<Navigate to="/dashboard/admin/approvals?tab=benefits" replace />} />
        <Route path="/dashboard/admin/approvals/expenses" element={<Navigate to="/dashboard/admin/approvals?tab=expenses" replace />} />
        <Route path="/dashboard/admin/approvals/history" element={<Navigate to="/dashboard/admin/approvals?tab=history" replace />} />

        {/* ====================================================== */}
        {/* Admin Settings Hub - TRUE CONSOLIDATION */}
        {/* ========================================= */}
        <Route
          path="/dashboard/admin/settings"
          element={
            <AdminRouteGuard>
              {withSuspense(<SettingsHubPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Legacy Settings Redirects */}
        <Route path="/dashboard/admin/settings/company" element={<Navigate to="/dashboard/admin/settings" replace />} />
        <Route path="/dashboard/admin/settings/time-attendance" element={<Navigate to="/dashboard/admin/settings?tab=time-attendance" replace />} />
        <Route path="/dashboard/admin/settings/organization" element={<Navigate to="/dashboard/admin/settings?tab=organization" replace />} />
        <Route path="/dashboard/admin/settings/export" element={<Navigate to="/dashboard/admin/settings?tab=export" replace />} />
        <Route path="/dashboard/admin/settings/integrations" element={<Navigate to="/dashboard/admin/settings?tab=integrations" replace />} />

        {/* System Monitoring - DEVELOPER ONLY (scans ALL tenants, not per-tenant) */}
        <Route
          path="/dashboard/admin/system-monitoring"
          element={
            <DeveloperGuard>
              {withSuspense(<SystemMonitoringPage />)}
            </DeveloperGuard>
          }
        />

        {/* Branding Settings - Tenant Admin */}
        <Route
          path="/dashboard/settings/branding"
          element={
            <AdminRouteGuard>
              {withSuspense(<BrandingSettingsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Branding Control Panel - DEVELOPER ONLY (views ALL tenants, EtchAre staff only) */}
        <Route
          path="/dashboard/admin/branding-control"
          element={
            <DeveloperGuard>
              {withSuspense(<BrandingControlPanelPage />)}
            </DeveloperGuard>
          }
        />

        {/* Knowledge Base Admin Hub - TRUE CONSOLIDATION */}
        <Route
          path="/dashboard/admin/knowledge-base"
          element={
            <AdminRouteGuard>
              {withSuspense(<KnowledgeBaseHubPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Legacy Knowledge Base Redirects */}
        <Route path="/dashboard/admin/knowledge-base/categories" element={<Navigate to="/dashboard/admin/knowledge-base?tab=categories" replace />} />
        <Route path="/dashboard/admin/knowledge-base/analytics" element={<Navigate to="/dashboard/admin/knowledge-base?tab=analytics" replace />} />
        {/* Knowledge Base Document Editor - Keep separate (complex editor) */}
        <Route
          path="/dashboard/admin/knowledge-base/documents/new"
          element={
            <AdminRouteGuard>
              {withSuspense(<DocumentEditorPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/knowledge-base/documents/:id/edit"
          element={
            <AdminRouteGuard>
              {withSuspense(<DocumentEditorPage />)}
            </AdminRouteGuard>
          }
        />

        {/* ============================================ */}
        {/* Employee Surveys - Anonymous engagement surveys */}
        {/* ============================================ */}

        {/* Employee: My Surveys - View assigned surveys */}
        <Route
          path={ROUTES.SURVEYS.MY_SURVEYS}
          element={withSuspense(<MySurveysPage />)}
        />

        {/* Employee: Take Survey - Token-based access */}
        <Route
          path={ROUTES.SURVEYS.PATTERNS.TAKE}
          element={withSuspense(<SurveyTakingPage />)}
        />

        {/* Admin: Survey Management Dashboard */}
        <Route
          path={ROUTES.SURVEYS.ADMIN}
          element={
            <AdminRouteGuard>
              {withSuspense(<SurveyAnalyticsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Admin: Create New Survey */}
        <Route
          path={ROUTES.SURVEYS.NEW}
          element={
            <AdminRouteGuard>
              {withSuspense(<SurveyBuilderPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Admin: Survey Analytics Detail */}
        <Route
          path={ROUTES.SURVEYS.PATTERNS.ANALYTICS}
          element={
            <AdminRouteGuard>
              {withSuspense(<SurveyDetailPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Announcements - Admin Routes */}
        <Route
          path="/dashboard/admin/announcements/new"
          element={
            <AdminRouteGuard>
              {withSuspense(<CreateAnnouncementPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Website CMS - Developer Routes (Developer emails only) */}
        {/* Website CMS - DISABLED for Launch (Standardizing on Hardcoded Content) */}
        {/* <Route
          path="/dashboard/admin/cms"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSManagementPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/grant-cms-access"
          element={withSuspense(<GrantCMSAccessPage />)}
        />
        <Route
          path="/dashboard/admin/cms/landing"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSLandingEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/pricing"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSPricingEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/features"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSFeaturesEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/about"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSAboutEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/contact"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSContactEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/global"
          element={
            <DeveloperGuard>
              {withSuspense(<CMSGlobalEditorPage />)}
            </DeveloperGuard>
          }
        /> */}

        {/* Visual WYSIWYG CMS Editor - DISABLED for Launch */}
        {/* <Route
          path="/dashboard/admin/cms/visual"
          element={
            <DeveloperGuard>
              {withSuspense(<VisualCMSEditorPage />)}
            </DeveloperGuard>
          }
        />
        <Route
          path="/dashboard/admin/cms/visual/page/:pageId"
          element={
            <DeveloperGuard>
              {withSuspense(<VisualCMSEditorPage />)}
            </DeveloperGuard>
          }
        /> */}

        {/* Developer Access Management - Developer only */}
        <Route
          path="/dashboard/admin/developer-access"
          element={
            <DeveloperGuard>
              {withSuspense(<DeveloperAccessPage />)}
            </DeveloperGuard>
          }
        />

        {/* Super Admin - Feature Flags Management (DEVELOPER ONLY - controls ALL tenants) */}
        <Route
          path="/dashboard/superadmin/feature-flags"
          element={
            <DeveloperGuard>
              {withSuspense(<FeatureFlagsPage />)}
            </DeveloperGuard>
          }
        />

        {/* Permissions Management - Owner/Admin only */}
        <Route
          path={ROUTES.ADMIN.PEOPLE.PERMISSIONS}
          element={
            <AdminRouteGuard>
              {withSuspense(<PermissionsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Promo Codes Management - DEVELOPER ONLY (platform-level tool) */}
        <Route
          path={ROUTES.ADMIN.PROMO_CODES}
          element={
            <DeveloperGuard>
              {withSuspense(<PromoCodesPage />)}
            </DeveloperGuard>
          }
        />

        {/* Founding Customers Management - DEVELOPER ONLY (platform-level tool) */}
        <Route
          path={ROUTES.ADMIN.FOUNDING_CUSTOMERS}
          element={
            <DeveloperGuard>
              {withSuspense(<FoundingCustomersPage />)}
            </DeveloperGuard>
          }
        />

        {/* Legacy Admin Settings Routes - Redirect to new consolidated Settings pages */}
        <Route
          path="/dashboard/admin/policies"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.TIME_ATTENDANCE} replace />}
        />

        <Route
          path="/dashboard/admin/holidays"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.TIME_ATTENDANCE} replace />}
        />

        <Route
          path="/dashboard/admin/organization"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.ORGANIZATION} replace />}
        />

        {/* Intermediate Route Redirects (for Breadcrumb navigation) */}
        <Route
          path="/dashboard/admin/hr"
          element={<Navigate to={ROUTES.ADMIN.HR.GOALS} replace />}
        />
        <Route
          path="/dashboard/admin/content"
          element={<Navigate to={ROUTES.ADMIN.CONTENT.KNOWLEDGE_BASE} replace />}
        />
        <Route
          path="/dashboard/admin/benefits"
          element={<Navigate to={ROUTES.ADMIN.BENEFITS.BASE} replace />}
        />

        {/* Finance Hub - Consolidated (TRUE CONSOLIDATION) */}
        {/* Payroll Export Disclaimer — must be acknowledged before accessing payroll exports */}
        <Route
          path="/dashboard/admin/finance/disclaimer"
          element={
            <AdminRouteGuard>
              <Suspense fallback={<Loader />}>
                <PayrollDisclaimerPage />
              </Suspense>
            </AdminRouteGuard>
          }
        />

        {/* All finance functionality in single page with URL-based tabs (?tab=bonuses) */}
        <Route
          path="/dashboard/admin/finance"
          element={
            <AdminRouteGuard>
              {withCompensationProtection(<FinanceHubPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Compensation Hub — Tip Pools (Operations+), Bonuses/Commissions/Analytics (Growth) */}
        <Route
          path="/dashboard/admin/compensation"
          element={
            <AdminRouteGuard>
              {withCompensationProtection(<CompensationHubPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Finance Hub - Legacy Path Redirects → compensation hub (except payroll stays in finance) */}
        <Route path="/dashboard/admin/finance/bonuses" element={<Navigate to="/dashboard/admin/compensation?tab=bonuses" replace />} />
        <Route path="/dashboard/admin/finance/tip-pools" element={<Navigate to="/dashboard/admin/compensation?tab=tip-pools" replace />} />
        <Route path="/dashboard/admin/finance/commissions" element={<Navigate to="/dashboard/admin/compensation?tab=commissions" replace />} />
        <Route path="/dashboard/admin/finance/payroll" element={<Navigate to="/dashboard/admin/finance" replace />} />
        <Route path="/dashboard/admin/finance/analytics" element={<Navigate to="/dashboard/admin/compensation?tab=analytics" replace />} />

        {/* Finance Hub - Standalone Routes (detail/form pages, outside hub) */}
        <Route
          path={ROUTES.ADMIN.COMPENSATION.DISCLAIMER}
          element={
            <AdminRouteGuard>
              {withCompensationProtection(<CompensationDisclaimerPage />)}
            </AdminRouteGuard>
          }
        />

        <Route
          path={ROUTES.ADMIN.COMPENSATION.SETTINGS}
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<CompensationSettingsPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path={ROUTES.ADMIN.COMPENSATION.REPORTS}
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<CompensationReportsPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path={ROUTES.ADMIN.COMPENSATION.PLAN_NEW}
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<CommissionPlanFormPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/admin/hr/compensation/plans/:id/edit"
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<CommissionPlanFormPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/admin/hr/compensation/plans/:id"
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<CommissionPlanViewPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/admin/hr/compensation/bonuses/:id"
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<BonusDetailPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path={ROUTES.ADMIN.COMPENSATION.TIP_POOL_NEW}
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<TipPoolFormPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/admin/hr/compensation/tip-pools/:id/edit"
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<TipPoolFormPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/dashboard/admin/hr/compensation/tip-pools/:id"
          element={
            <AdminRouteGuard>
              <CompensationDisclaimerGuard>
                {withCompensationProtection(<TipPoolViewPage />)}
              </CompensationDisclaimerGuard>
            </AdminRouteGuard>
          }
        />

        {/* Legacy route redirect - Old compensation base to new Compensation Hub */}
        <Route
          path={ROUTES.ADMIN.COMPENSATION.BASE}
          element={<Navigate to={ROUTES.ADMIN.COMPENSATION.HUB} replace />}
        />

        {/* Legacy Payroll Export Page redirect */}
        <Route
          path="/dashboard/payroll"
          element={<Navigate to="/dashboard/admin/finance" replace />}
        />

        {/* Legacy time-tracking settings redirect */}
        <Route
          path="/dashboard/admin/time-tracking"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.TIME_ATTENDANCE} replace />}
        />

        {/* Kiosk Hub - Consolidated (TRUE CONSOLIDATION) */}
        {/* All kiosk functionality in single page with URL-based tabs (?tab=pins) */}
        <Route
          path="/dashboard/admin/kiosk"
          element={
            <AdminRouteGuard>
              {withSuspense(<KioskHubPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Kiosk Hub - Legacy Path Redirects (old nested routes → query params) */}
        <Route path="/dashboard/admin/kiosk/overview" element={<Navigate to="/dashboard/admin/kiosk" replace />} />
        <Route path="/dashboard/admin/kiosk/pin-management" element={<Navigate to="/dashboard/admin/kiosk?tab=pins" replace />} />
        <Route path="/dashboard/admin/kiosk/pins" element={<Navigate to="/dashboard/admin/kiosk?tab=pins" replace />} />
        <Route path="/dashboard/admin/kiosk/monitoring" element={<Navigate to="/dashboard/admin/kiosk?tab=monitoring" replace />} />
        <Route path="/dashboard/admin/kiosk/settings" element={<Navigate to="/dashboard/admin/kiosk?tab=settings" replace />} />

        {/* Leave Settings - Admin Routes */}
        <Route
          path="/dashboard/admin/leave-settings"
          element={
            <AdminRouteGuard>
              <Navigate to="/dashboard/admin/leave-settings/policies" replace />
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/leave-settings/policies"
          element={
            <AdminRouteGuard>
              {withSuspense(<LeavePoliciesPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/leave-settings/calendars"
          element={
            <AdminRouteGuard>
              {withSuspense(<HolidayCalendarsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Compliance Dashboard - Admin Route */}
        <Route
          path="/dashboard/admin/compliance"
          element={
            <AdminRouteGuard>
              {withSuspense(<ComplianceDashboardPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Recruiting Hub - Consolidated (TRUE CONSOLIDATION) */}
        <Route
          path="/dashboard/recruiting"
          element={
            <AdminRouteGuard>
              <ErrorBoundary>
                {withSuspense(<RecruitingHubPage />)}
              </ErrorBoundary>
            </AdminRouteGuard>
          }
        />
        {/* Legacy redirects - Recruiting list pages consolidated into RecruitingHub */}
        <Route path="/dashboard/recruiting/jobs" element={<Navigate to="/dashboard/recruiting?tab=jobs" replace />} />
        <Route path="/dashboard/recruiting/candidates" element={<Navigate to="/dashboard/recruiting?tab=candidates" replace />} />
        <Route path="/dashboard/recruiting/interviews" element={<Navigate to="/dashboard/recruiting?tab=interviews" replace />} />
        <Route path="/dashboard/recruiting/offers" element={<Navigate to="/dashboard/recruiting?tab=offers" replace />} />
        <Route path="/dashboard/recruiting/talent-pool" element={<Navigate to="/dashboard/recruiting?tab=talent-pool" replace />} />
        <Route path="/dashboard/recruiting/pipeline" element={<Navigate to="/dashboard/recruiting?tab=pipeline" replace />} />

        {/* ATS/Recruiting - Detail/Create Routes (outside hub) */}
        <Route
          path="/dashboard/recruiting/jobs/new"
          element={
            <AdminRouteGuard>
              {withSuspense(<CreateJobPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/recruiting/jobs/:id"
          element={
            <AdminRouteGuard>
              {withSuspense(<JobDetailPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/recruiting/jobs/:id/edit"
          element={
            <AdminRouteGuard>
              {withSuspense(<CreateJobPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/recruiting/candidates/:candidateId"
          element={
            <AdminRouteGuard>
              {withSuspense(<CandidateDetailPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Pipeline with job - redirect to pipeline tab (job selection now in hub) */}
        <Route path="/dashboard/recruiting/pipeline/:jobId" element={<Navigate to="/dashboard/recruiting?tab=pipeline" replace />} />
        <Route
          path="/dashboard/recruiting/interviews/schedule"
          element={
            <AdminRouteGuard>
              {withSuspense(<ScheduleInterviewPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/recruiting/offers/new"
          element={
            <AdminRouteGuard>
              {withSuspense(<CreateOfferPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Offer detail - redirect to offers tab (no separate detail page exists) */}
        <Route path="/dashboard/recruiting/offers/:offerId" element={<Navigate to="/dashboard/recruiting?tab=offers" replace />} />

        {/* ====================================================== */}
        {/* Goals & OKRs Routes (KPI/OKR Phase 2) */}
        {/* ====================================================== */}

        {/* Employee Goals - All authenticated users */}
        <Route
          path={ROUTES.MY_WORK.GOALS}
          element={withSuspense(<MyGoalsPage />)}
        />
        <Route
          path="/dashboard/me/goals/new"
          element={withSuspense(<CreateGoalPage />)}
        />
        <Route
          path="/dashboard/me/goals/:goalId"
          element={withSuspense(<GoalDetailPage />)}
        />

        {/* Team Goals - Manager/Admin only */}
        <Route
          path={ROUTES.TEAM.GOALS}
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<TeamGoalsPage />)}
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path="/dashboard/team/goals/:goalId"
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<GoalDetailPage />)}
            </ManagerPlusRouteGuard>
          }
        />

        {/* Admin Goals Management - HR Admin/Owner only */}
        <Route
          path={ROUTES.ADMIN.HR.GOALS}
          element={
            <AdminRouteGuard>
              {withSuspense(<GoalsAdminPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/hr/goals/cycles"
          element={
            <AdminRouteGuard>
              {withSuspense(<ManageCyclesPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/hr/goals/:goalId"
          element={
            <AdminRouteGuard>
              {withSuspense(<GoalDetailPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Learning Hub - Consolidated (TRUE CONSOLIDATION) */}
        {/* All learning functionality in single page with URL-based tabs (?tab=catalog) */}
        <Route
          path="/dashboard/learning"
          element={withSuspense(<LearningHubPage />)}
        />
        {/* Learning Hub - Legacy Path Redirects (old nested routes → query params) */}
        <Route path="/dashboard/learning/my-learning" element={<Navigate to="/dashboard/learning" replace />} />
        <Route path="/dashboard/learning/catalog" element={<Navigate to="/dashboard/learning?tab=catalog" replace />} />
        <Route path="/dashboard/learning/certificates" element={<Navigate to="/dashboard/learning?tab=certificates" replace />} />
        <Route path="/dashboard/learning/quiz-history" element={<Navigate to="/dashboard/learning?tab=quiz-history" replace />} />

        {/* Standalone Training Routes (outside hub) */}
        <Route
          path={ROUTES.TRAINING.PATTERNS.COURSE_DETAIL}
          element={withSuspense(<CourseDetailPage />)}
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.COURSE_PLAYER}
          element={withSuspense(<CoursePlayerPage />)}
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_PLAYER}
          element={withSuspense(<QuizPlayerPage />)}
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_PLAYER_WITH_ENROLLMENT}
          element={withSuspense(<QuizPlayerPage />)}
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_RESULTS}
          element={withSuspense(<QuizResultsPage />)}
        />

        {/* Admin routes (HR Admin / Owner only) */}
        <Route
          path={ROUTES.TRAINING.ADMIN}
          element={
            <AdminRouteGuard>
              {withSuspense(<AdminCoursesPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.COURSE_BUILDER}
          element={
            <AdminRouteGuard>
              {withSuspense(<CourseBuilderPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.COURSE_EDIT}
          element={
            <AdminRouteGuard>
              {withSuspense(<CourseBuilderPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_BUILDER}
          element={
            <AdminRouteGuard>
              {withSuspense(<QuizBuilderPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_EDIT}
          element={
            <AdminRouteGuard>
              {withSuspense(<QuizBuilderPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.PATTERNS.QUIZ_ANALYTICS}
          element={
            <AdminRouteGuard>
              {withSuspense(<QuizAnalyticsPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.ANALYTICS}
          element={
            <AdminRouteGuard>
              {withSuspense(<TrainingAnalyticsDashboardPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={ROUTES.TRAINING.SETTINGS}
          element={
            <AdminRouteGuard>
              {withSuspense(<LMSSettingsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Performance Reviews Routes */}
        {/* Employee routes - My Reviews */}
        <Route
          path={ROUTES.PERFORMANCE_REVIEWS.MY_REVIEWS}
          element={withSuspense(<MyReviewsPage />)}
        />

        {/* Employee routes - Self-Assessment (must be before :reviewId to match first) */}
        <Route
          path="/dashboard/me/reviews/self-assessment/:reviewId"
          element={withSuspense(<SelfAssessmentPage />)}
        />

        {/* Manager routes - Complete / deliver review for direct report */}
        <Route
          path={`${ROUTES.PERFORMANCE_REVIEWS.ADMIN}/review/:reviewId`}
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<ManagerReviewPage />)}
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path={`${ROUTES.PERFORMANCE_REVIEWS.ADMIN}/deliver/:reviewId`}
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<ManagerReviewPage />)}
            </ManagerPlusRouteGuard>
          }
        />

        {/* Legacy manager route (kept for backcompat) */}
        <Route
          path="/dashboard/reviews/:reviewId"
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<ManagerReviewPage />)}
            </ManagerPlusRouteGuard>
          }
        />

        {/* Admin routes - Review Cycles Management */}
        <Route
          path={ROUTES.PERFORMANCE_REVIEWS.ADMIN}
          element={
            <AdminRouteGuard>
              {withSuspense(<AdminReviewCyclesPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Modern path redirect for performance reviews */}
        <Route
          path={ROUTES.ADMIN.HR.REVIEWS}
          element={<Navigate to={ROUTES.PERFORMANCE_REVIEWS.ADMIN} replace />}
        />
        {/* Legacy admin shifts redirect to scheduling */}
        <Route
          path="/dashboard/admin/shifts"
          element={<Navigate to="/dashboard/scheduling/weekly" replace />}
        />

        {/* Legacy Settings Routes - Redirect to new consolidated Settings pages */}
        <Route
          path="/dashboard/settings/holidays"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.TIME_ATTENDANCE} replace />}
        />

        <Route
          path="/dashboard/settings/leave-policies"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.TIME_ATTENDANCE} replace />}
        />

        <Route
          path="/dashboard/settings/organization"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.ORGANIZATION} replace />}
        />

        {/* Owner-only Routes - Legacy roles redirect to Organization Structure settings */}
        <Route
          path="/dashboard/roles"
          element={<Navigate to={ROUTES.ADMIN.SETTINGS.ORGANIZATION} replace />}
        />

        {/* Billing - OWNER ONLY (not hr_admin) */}
        <Route
          path="/dashboard/billing"
          element={
            <OwnerRouteGuard>
              {withSuspense(<BillingPage />)}
            </OwnerRouteGuard>
          }
        />

        {/* Audit Log - Owner only */}
        <Route
          path={ROUTES.ADMIN.AUDIT_LOG}
          element={
            <OwnerRouteGuard>
              {withSuspense(<AuditLogPage />)}
            </OwnerRouteGuard>
          }
        />

        {/* Asset Management - Admin only */}
        <Route
          path={ROUTES.ADMIN.HR.ASSETS}
          element={
            <AdminRouteGuard>
              {withSuspense(<AssetInventoryPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path={`${ROUTES.ADMIN.HR.ASSETS}/:id`}
          element={
            <AdminRouteGuard>
              {withSuspense(<AssetDetailPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Employee welcome - accessible for new employees after invite redemption */}
        <Route path="/dashboard/welcome" element={withSuspense(<EmployeeWelcomePage />)} />

        {/* General Routes */}
        <Route path="/dashboard/leave" element={withSuspense(<LeavePage />)} />
        <Route path="/dashboard/leave/:id" element={withSuspense(<LeaveDetails />)} />
        {/* Legacy redirect - Leave approvals consolidated into ApprovalsHub */}
        <Route path="/dashboard/leave/approvals" element={<Navigate to="/dashboard/admin/approvals?tab=leave" replace />} />
        <Route
          path="/dashboard/leave/forecast"
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<TeamForecastPage />)}
            </ManagerPlusRouteGuard>
          }
        />
        <Route path="/dashboard/assistant" element={withSuspense(<Assistant />)} />
        <Route path="/dashboard/activity" element={withSuspense(<ActivityPage />)} />
        <Route path="/dashboard/notifications" element={withSuspense(<NotificationsPage />)} />

        {/* Manager+ Routes */}
        {/* Legacy MY_TEAM route → redirect to My Team hub */}
        <Route
          path={ROUTES.MY_TEAM}
          element={<Navigate to="/dashboard/my-team" replace />}
        />
        <Route
          path={ROUTES.TRAINING.TEAM_QUIZ_PROGRESS}
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<TeamQuizProgressPage />)}
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path="/dashboard/team-time"
          element={<Navigate to="/dashboard/reports?tab=team-time" replace />}
        />

        {/* Employee-facing Routes */}
        {/* Profile routes - consolidated: Redirect to Unified Profile */}
        <Route path="/dashboard/profile" element={<RedirectToMyProfile />} />
        <Route path="/dashboard/profile/edit" element={<RedirectToMyProfile />} />

        {/* Directory Page - Restored */}
        <Route
          path={ROUTES.PEOPLE}
          element={withSuspense(<DirectoryPage />)}
        />

        {/* Employee profile routes - legacy admin route redirect */}
        <Route path="/dashboard/admin/employees/:id" element={<RedirectEmployeeProfile />} />
        {/* Legacy route redirect (backward compatibility) */}
        <Route path="/dashboard/employees/:id" element={<RedirectEmployeeProfile />} />
        {/* Note: /dashboard/people/:id now shows SocialProfilePage (see route above) */}

        {/* Documents Module - New Routes */}
        <Route path="/dashboard/me/documents" element={withSuspense(<MyDocumentsPage />)} />
        <Route
          path="/dashboard/admin/documents"
          element={
            <AdminRouteGuard>
              {withSuspense(<DocumentHubPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Old /dashboard/documents → role-based redirect */}
        <Route path="/dashboard/documents" element={withSuspense(<DocumentsPage />)} />
        <Route path="/dashboard/compensation" element={withCompensationProtection(<EmployeeBonusesPage />)} />
        <Route path="/dashboard/vacation" element={<Navigate to="/dashboard/leave" replace />} />
        {/* Help Center - Wikipedia-style documentation */}
        <Route path="/dashboard/help" element={withSuspense(<DocsLandingPage />)} />
        <Route path="/dashboard/help/:categoryId" element={withSuspense(<DocsCategoryPage />)} />
        <Route path="/dashboard/help/:categoryId/:slug" element={withSuspense(<DocsArticlePage />)} />


        {/* Payroll Legacy Redirect */}
        <Route
          path={ROUTES.ADMIN.PAYROLL}
          element={<Navigate to="/dashboard/admin/finance" replace />}
        />

        {/* Knowledge Base - Employee Routes */}
        <Route path="/dashboard/knowledge-base" element={withSuspense(<KnowledgeBaseHomePage />)} />
        <Route path="/dashboard/knowledge-base/search" element={withSuspense(<SearchResultsPage />)} />
        <Route path="/dashboard/knowledge-base/documents/:id" element={withSuspense(<DocumentViewPage />)} />
        <Route path="/dashboard/knowledge-base/categories/:id" element={withSuspense(<CategoryBrowsePage />)} />

        {/* Announcements - Employee Routes */}
        <Route path="/dashboard/announcements" element={withSuspense(<AnnouncementFeedPage />)} />
        <Route path="/dashboard/announcements/:id" element={withSuspense(<AnnouncementDetailPage />)} />

        {/* Benefits Management - HR/Admin Routes (redirect to consolidated routes) */}
        <Route
          path="/dashboard/benefits/approvals"
          element={<Navigate to="/dashboard/admin/hr/benefits/enrollments?status=pending" replace />}
        />
        <Route
          path="/dashboard/benefits/history"
          element={<Navigate to="/dashboard/admin/hr/benefits/enrollments" replace />}
        />

        {/* Benefits Admin Hub - TRUE CONSOLIDATION */}
        <Route
          path="/dashboard/admin/hr/benefits"
          element={
            <AdminRouteGuard>
              {withSuspense(<BenefitsAdminHubPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Add/Edit Benefit Type form - Keep separate (complex form) */}
        <Route
          path="/dashboard/admin/hr/benefits/types/new"
          element={
            <AdminRouteGuard>
              {withSuspense(<BenefitTypeFormPage />)}
            </AdminRouteGuard>
          }
        />
        <Route
          path="/dashboard/admin/hr/benefits/types/:id/edit"
          element={
            <AdminRouteGuard>
              {withSuspense(<BenefitTypeFormPage />)}
            </AdminRouteGuard>
          }
        />
        {/* Legacy Benefits Redirects */}
        <Route path="/dashboard/admin/hr/benefits/enrollments" element={<Navigate to="/dashboard/admin/hr/benefits?tab=enrollments" replace />} />
        <Route path="/dashboard/admin/hr/benefits/types" element={<Navigate to="/dashboard/admin/hr/benefits" replace />} />
        <Route path="/dashboard/admin/hr/benefits/approvals" element={<Navigate to="/dashboard/admin/hr/benefits?tab=enrollments" replace />} />
        <Route path="/dashboard/admin/hr/benefits/history" element={<Navigate to="/dashboard/admin/hr/benefits?tab=enrollments" replace />} />
        <Route path="/dashboard/admin/hr/benefits/settings" element={<Navigate to="/dashboard/admin/hr/benefits" replace />} />

        {/* Benefits Management - Employee Routes (all authenticated users) */}
        <Route
          path="/dashboard/benefits/enrollment"
          element={withSuspense(<BenefitsEnrollmentPage />)}
        />
        <Route
          path="/dashboard/benefits/dependents"
          element={withSuspense(<DependentsPage />)}
        />
        <Route
          path="/dashboard/benefits/my-benefits"
          element={withSuspense(<MyBenefitsPage />)}
        />

        {/* Expense Management - Employee Routes (German-compliant expense reporting) */}
        <Route
          path="/dashboard/expenses"
          element={withSuspense(<MyExpenseReportsPage />)}
        />
        <Route
          path="/dashboard/expenses/new"
          element={withSuspense(<CreateExpenseReportPage />)}
        />
        <Route
          path="/dashboard/expenses/:reportId"
          element={withSuspense(<ExpenseReportDetailPage />)}
        />

        {/* Expense Management Hub - PRIMARY entry point */}
        <Route
          path={ROUTES.ADMIN.HR.EXPENSE_HUB}
          element={
            <AdminRouteGuard>
              {withSuspense(<ExpensesHubPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Expense Analytics - Admin Route (standalone, also reachable from hub) */}
        <Route
          path={ROUTES.ADMIN.HR.EXPENSE_ANALYTICS}
          element={
            <AdminRouteGuard>
              {withSuspense(<ExpenseAnalyticsPage />)}
            </AdminRouteGuard>
          }
        />

        {/* Expense Settings - Admin Route (standalone, also reachable from hub) */}
        <Route
          path={ROUTES.ADMIN.HR.EXPENSE_SETTINGS}
          element={
            <AdminRouteGuard>
              {withSuspense(<ExpenseSettingsPage />)}
            </AdminRouteGuard>
          }
        />



        {/* German-compliant time tracking */}
        <Route path="/dashboard/time" element={withSuspense(<ShiftTrackingPage />)} />
        <Route path="/dashboard/time/my-schedule" element={withSuspense(<MySchedulePage />)} />
        <Route
          path="/dashboard/time/schedule"
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<SchedulePage />)}
            </ManagerPlusRouteGuard>
          }
        />
        {/* Legacy redirect - Shift approvals consolidated into ApprovalsHub */}
        <Route path="/dashboard/time/approvals" element={<Navigate to="/dashboard/admin/approvals?tab=shifts" replace />} />
        <Route
          path="/dashboard/time/settings"
          element={
            <RolesRouteGuard>
              {withSuspense(<TimeTrackingSettingsPage />)}
            </RolesRouteGuard>
          }
        />

        {/* Scheduling Section - Single overview page with tabs (Manager+) */}
        <Route
          path="/dashboard/scheduling"
          element={
            <ManagerPlusRouteGuard>
              <Navigate to="/dashboard/scheduling/overview" replace />
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path="/dashboard/scheduling/weekly"
          element={
            <ManagerPlusRouteGuard>
              <Navigate to="/dashboard/scheduling/overview?tab=weekly" replace />
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path="/dashboard/scheduling/templates"
          element={
            <ManagerPlusRouteGuard>
              <Navigate to="/dashboard/scheduling/overview?tab=templates" replace />
            </ManagerPlusRouteGuard>
          }
        />
        <Route
          path="/dashboard/scheduling/overview"
          element={
            <ManagerPlusRouteGuard>
              {withSuspense(<SchedulePage />)}
            </ManagerPlusRouteGuard>
          }
        />

        {/* Legacy redirects - all redirect to canonical /dashboard routes */}
        <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
        <Route path="/profile" element={<Navigate to="/dashboard/profile" replace />} />
        <Route path="/profile/details" element={<Navigate to="/dashboard/profile" replace />} />
        <Route path="/directory" element={<Navigate to="/dashboard/people" replace />} />
        <Route path="/people/:employeeId" element={<RedirectToMyProfile />} />
        <Route path="/employees/:id" element={<RedirectEmployeeProfile />} />
        <Route path="/documents" element={<Navigate to="/dashboard/me/documents" replace />} />
        <Route path="/payslips" element={<Navigate to="/dashboard/me/documents?tab=payslips" replace />} />
        <Route path="/dashboard/payslips" element={<Navigate to="/dashboard/me/documents?tab=payslips" replace />} />
        <Route path="/colleagues" element={<Navigate to="/dashboard/people" replace />} />
        <Route path="/vacation" element={<Navigate to="/dashboard/vacation" replace />} />
        <Route path="/time" element={<Navigate to="/dashboard/time" replace />} />
        <Route path="/leave/:id" element={<RedirectLeaveDetails />} />
        <Route path="/leave/approvals" element={<Navigate to="/dashboard/leave/approvals" replace />} />
        <Route path="/activity" element={<Navigate to="/dashboard/activity" replace />} />
        <Route path="/setup" element={<Navigate to="/dashboard/setup" replace />} />
      </Route>
      {/* ============================================ */}
      {/* DEV TOOLS - LOCAL DEVELOPMENT ONLY          */}
      {/* These routes are STRICTLY for local dev     */}
      {/* They will NEVER deploy to staging/production */}
      {/* ============================================ */}

      {/* Dev Console - LOCAL ONLY (dev mode required) */}
      {import.meta.env.DEV ? (
        <>
          <Route path="/dev/console" element={withSuspense(<DevConsolePage />)} />
          <Route path="/dev/seed" element={withSuspense(<DevSeedPage />)} />
        </>
      ) : null}

      {/* Dev Health - Available in non-production (for debugging) */}
      {import.meta.env.MODE !== 'production' ? (
        <Route path="/dev/health" element={withSuspense(<DevHealthPage />)} />
      ) : null}
      {/* Redirects for common mislinked paths */}
      <Route path="/dashboard/approvals" element={<Navigate to="/dashboard/admin/approvals" replace />} />
      <Route path="/dashboard/leave/request" element={<Navigate to="/dashboard/leave" replace />} />
      <Route path="/dashboard/leave/calendar" element={<Navigate to="/dashboard/leave/forecast" replace />} />

      <Route path="*" element={<Navigate to="/404" replace />} />
    </Route>
  )
);

export default router;

// Force HMR
